import { faPaperPlaneAlt, faWarning } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import debounce from 'lodash/debounce';
import flatten from 'lodash/flatten';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import styled from 'styled-components';
import {
  App,
  ChatRoomKind,
  useChatLazyQuery,
  useCoachMemberListLazyQuery,
  useCreateChatMutation,
  useSelfQuery,
} from '../../graphql/types';
import { WHITE } from '../../theme';
import {
  EventCategories,
  EventLabels,
  EventProfileCardNames,
} from '../../utils/trackingEvents';
import LoadingPanel from '../LoadingPanel';
import Button, { ButtonVariant } from '../reusable/Button';
import { Divider } from '../reusable/Divider';
import { MemberAutocomplete } from '../reusable/MemberAutocomplete';
import { SnackbarVariant } from '../reusable/Snackbar';
import { useSnackbarContext } from '../reusable/Snackbar/SnackbarContextProvider';
import TextAreaInput from '../reusable/TextAreaInput';
import { ChatSize, useChatContext } from './Context';
import { Header } from './Header';

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
`;

const StyledFormContent = styled.div`
  display: grid;
  grid-template-rows: auto 1fr;
  padding: 8px;
  gap: 8px;
  height: 100%;
`;

const StyledFormFooter = styled.div`
  display: flex;
  justify-content: end;
  padding: 8px;
`;

interface CreateChatValues {
  memberId: string;
  initialMessage: string;
}

interface CreateChatProps {
  closeable?: boolean;
}

export const CreateChat: React.FC<CreateChatProps> = ({ closeable }) => {
  const methods = useForm<CreateChatValues>();
  const [isLoadingSelecterMemberChat, setIsLoadingSelecterMemberChat] =
    useState(false);
  const { handleSubmit, watch, formState, setValue } = methods;
  const {
    openChat,
    chatSize,
    closeChat,
    member: passedInMember,
  } = useChatContext();
  const { setSnackbar } = useSnackbarContext();

  const [createChat] = useCreateChatMutation();

  const memberIdWatcher = watch('memberId');

  const { data: selfData } = useSelfQuery();
  const selfId = selfData?.self?.id;
  const [
    fetchCoachMemberList,
    { data: userListData, loading: userListLoading },
  ] = useCoachMemberListLazyQuery();
  const membersFilter = useMemo(
    () => ({
      hasSignedIn: true,
    }),
    [],
  );

  const [fetchChat] = useChatLazyQuery();

  const usersList = useMemo(() => {
    const allClients = userListData?.self?.coachData?.clients.edges.map(
      ({ node }) => node,
    );
    const allMembers = allClients?.map((client) =>
      client.users.edges.map(({ node }) => node),
    );

    const flattenedList = flatten(allMembers);
    return flattenedList;
  }, [userListData?.self?.coachData?.clients.edges]);

  const handleFormSubmit = async (values: CreateChatValues) => {
    const { memberId, initialMessage } = values;
    if (!selfId) {
      return;
    }
    const createdChat = await createChat({
      variables: {
        input: {
          name: `Coach created: ${memberId}`,
          users: [selfId, memberId],
          app: App.Heart,
          kind: ChatRoomKind.Direct,
          initialMessage,
        },
      },
      refetchQueries: ['chats'],
      onError: () =>
        setSnackbar({
          icon: faWarning,
          message: 'An error occured while creating the chat.',
          variant: SnackbarVariant.Danger,
        }),
    });

    if (!createdChat.errors) {
      closeChat();
      const chatId = createdChat.data?.createChatRoom.chatRoom?.id;
      const chatMember = usersList?.find((user) => user.id === memberId);
      if (chatId) {
        openChat({
          chatId,
          member: chatMember,
        });
      }
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: biome 2.X migration
  useEffect(() => {
    // on mount, fetch the member list
    if (chatSize !== ChatSize.Closed) {
      fetchCoachMemberList({
        variables: {
          input: {
            first: 10,
          },
          membersFilter,
        },
      });
    }
    if (chatSize === ChatSize.Closed) {
      methods.reset();
    }
  }, [fetchCoachMemberList, chatSize, methods]);

  const fetchSelectedMemberChat = useCallback(
    async (memberId: string) => {
      if (selfId) {
        setIsLoadingSelecterMemberChat(true);
        const result = await fetchChat({
          variables: {
            userIds: [memberId, selfId],
          },
        });
        const chat = result.data?.chatRoom;
        if (chat) {
          openChat({
            chatId: chat.id,
            member:
              passedInMember ?? usersList?.find((user) => user.id === memberId),
            updateChatSize: false,
          });
        } else {
          setIsLoadingSelecterMemberChat(false);
        }
      }
    },
    [fetchChat, openChat, selfId, usersList, passedInMember],
  );

  useEffect(() => {
    if (passedInMember?.id) {
      setValue('memberId', passedInMember?.id);
      fetchSelectedMemberChat(passedInMember.id);
    }
  }, [passedInMember?.id, fetchSelectedMemberChat, setValue]);

  const handleAutocompleteInput = debounce((value: string) => {
    fetchCoachMemberList({
      variables: {
        membersFilter: {
          ...(value ? { name: value } : {}),
          ...membersFilter,
        },
        input: {
          first: 10,
        },
      },
    });
  }, 500);

  return (
    <>
      <Header
        content={{ title: 'New message', hideEmptyElements: true }}
        closeable={closeable}
      />
      <FormProvider {...methods}>
        <StyledForm
          onSubmit={handleSubmit(handleFormSubmit)}
          data-testid="createChat-form"
        >
          <StyledFormContent>
            <Controller
              name="memberId"
              rules={{
                required: 'Member is required',
              }}
              render={({ field }) => (
                <MemberAutocomplete
                  disabled={
                    Boolean(passedInMember) || isLoadingSelecterMemberChat
                  }
                  name={field.name}
                  selectedUserId={passedInMember?.id ?? field.value}
                  users={usersList || []}
                  onInputChange={handleAutocompleteInput}
                  onSelection={async (user) => {
                    field.onChange(user?.id || '');
                    if (user?.id) {
                      await fetchSelectedMemberChat(user.id);
                    }
                  }}
                  placeholder="Search for a member"
                  loading={userListLoading}
                  trackingData={{
                    event_category: EventCategories.Chat,
                    card_name: EventProfileCardNames.CreateChat,
                  }}
                />
              )}
            />
            {isLoadingSelecterMemberChat && (
              <LoadingPanel noBorder fullHeight />
            )}
            {memberIdWatcher && !isLoadingSelecterMemberChat && (
              <TextAreaInput
                formRegister={methods.register('initialMessage', {
                  required: 'Message is required',
                })}
                placeholder="Write a message"
                backgroundColor={WHITE}
                height="100%"
                trackingData={{
                  event_category: EventCategories.Chat,
                  card_name: EventProfileCardNames.CreateChat,
                  event_label: EventLabels.InitialMessage,
                }}
              />
            )}
          </StyledFormContent>

          <Divider $marginBottom={0} $marginTop={0} />

          <StyledFormFooter>
            <Button
              type="submit"
              variant={ButtonVariant.Dark}
              icon={<FontAwesomeIcon icon={faPaperPlaneAlt} />}
              trackingData={{
                event_category: EventCategories.Chat,
                card_name: EventProfileCardNames.CreateChat,
                event_label: EventLabels.Create,
              }}
              disabled={formState.isSubmitting}
              loading={formState.isSubmitting}
            >
              Send
            </Button>
          </StyledFormFooter>
        </StyledForm>
      </FormProvider>
    </>
  );
};
